import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, fmtDate, formatApiError, priorityColors, statusColors } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Edit3, Wrench, Check } from "lucide-react";
import { toast } from "sonner";

const empty = () => ({ title: "", category: "", property_id: "", apartment_id: "", priority: "media", due_date: "", assignee_id: "", assignee_name: "", status: "da_fare", notes: "" });

export default function Todo() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [t, a] = await Promise.all([http.get("/tasks"), http.get("/apartments")]);
    setItems(t.data); setApartments(a.data);
    try { const u = await http.get("/auth/users"); setUsers(u.data); } catch {}
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("new") === "1") { setForm(empty()); setEditId(null); setOpen(true); params.delete("new"); setParams(params, { replace: true }); } }, [params, setParams]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    ["property_id", "apartment_id", "assignee_id", "due_date", "category"].forEach((k) => { if (!payload[k]) payload[k] = null; });
    try {
      if (editId) await http.put(`/tasks/${editId}`, payload); else await http.post("/tasks", payload);
      toast.success("Salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleStatus = async (t) => { await http.put(`/tasks/${t.id}`, { ...t, status: t.status === "completato" ? "da_fare" : "completato" }); load(); };
  const convertToMaint = async (id) => { await http.post(`/tasks/${id}/convert-to-maintenance`); toast.success("Convertito in manutenzione"); load(); };
  const onDelete = async (id) => { if (!window.confirm("Eliminare?")) return; await http.delete(`/tasks/${id}`); load(); };
  const aptName = (id) => apartments.find((a) => a.id === id)?.name || "";

  return (
    <div className="space-y-6" data-testid="todo-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--terracotta)] font-semibold">Todo</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Attività & Promemoria</h1>
        </div>
        <Button data-testid="new-task-btn" onClick={() => { setForm(empty()); setEditId(null); setOpen(true); }} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]"><Plus className="h-4 w-4 mr-1" /> Nuovo task</Button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <div className="text-center text-slate-400 py-10">Nessun task.</div>}
        {items.map((t) => (
          <Card key={t.id} className={`rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3 ${t.status==="completato" ? "opacity-60" : ""}`} data-testid={`task-${t.id}`}>
            <button onClick={() => toggleStatus(t)} data-testid={`task-toggle-${t.id}`} className={`shrink-0 h-7 w-7 rounded-full border-2 flex items-center justify-center ${t.status==="completato" ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300"}`}>{t.status==="completato" && <Check className="h-4 w-4" />}</button>
            <div className="flex-1 min-w-0">
              <div className={`font-medium ${t.status==="completato" ? "line-through text-slate-500" : "text-slate-900"}`}>{t.title}</div>
              <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-3">
                {t.due_date && <span>Scadenza: {fmtDate(t.due_date)}</span>}
                {aptName(t.apartment_id) && <span>{aptName(t.apartment_id)}</span>}
                {t.assignee_name && <span>{t.assignee_name}</span>}
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <Badge variant="outline" className={priorityColors[t.priority]}>{t.priority}</Badge>
              <Badge variant="outline" className={statusColors[t.status]}>{t.status.replace("_"," ")}</Badge>
              <button onClick={() => convertToMaint(t.id)} data-testid={`task-convert-${t.id}`} title="Converti in manutenzione" className="p-2 text-slate-500 hover:text-amber-600"><Wrench className="h-4 w-4" /></button>
              <button onClick={() => { setForm({ ...empty(), ...t }); setEditId(t.id); setOpen(true); }} className="p-2 text-slate-500 hover:text-teal-700"><Edit3 className="h-4 w-4" /></button>
              <button onClick={() => onDelete(t.id)} className="p-2 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Modifica" : "Nuovo"} task</DialogTitle></DialogHeader>
          <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Label>Attività</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="task-title" /></div>
            <div><Label>Appartamento (facoltativo)</Label>
              <Select value={form.apartment_id || ""} onValueChange={(v) => setForm({ ...form, apartment_id: v })}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Scadenza</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div><Label>Assegnato a</Label>
              <Select value={form.assignee_id || ""} onValueChange={(v) => { const u = users.find((x) => x.id === v); setForm({ ...form, assignee_id: v, assignee_name: u?.name || form.assignee_name }); }}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Priorità</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["bassa","media","alta","urgente"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="md:col-span-2"><Label>Note</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter className="md:col-span-2"><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button type="submit" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="task-submit">Salva</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
